// ================================================================
// 個案詳情頁面（查看資料 + 問問題 + 配對分析 + 歷史記錄）
// ================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack,
  AutoAwesome,
  Logout,
  Person,
  Female,
  Male,
  Search,
  PlayArrow,
  ExpandMore,
  History,
  PersonAdd,
  Delete,
  Favorite,
  Download,
} from '@mui/icons-material';
import { useTeacher } from '../../contexts/TeacherContext';
import {
  getConsultee,
  askQuestion,
  getConsultationHistory,
  teacherPairAnalysis,
  Consultee,
  Consultation,
  PersonInfo,
  PairResponse,
} from '../../api/teacher';
import { getQuestions, Question, QuestionCategory } from '../../api/questions';
import ReportContent from '../../components/ReportContent';

// ================================================================
// 常數
// ================================================================

const CATEGORIES: { id: QuestionCategory; label: string; color: string }[] = [
  { id: 'MAIN',   label: '熱門 (NTD 99)',    color: '#667eea' },
  { id: 'COMMON', label: '一般 (NTD 49)',    color: '#48bb78' },
  { id: 'FULL',   label: '完整 (NTD 399)',   color: '#ed8936' },
  { id: 'SIMPLE', label: '簡單 (免費/10)',   color: '#9e9e9e' },
  { id: 'PAIR',   label: '配對 (不扣款)',    color: '#e91e8c' },
];

// 13 個選項：子時分兩段（00:01-01:00 需轉前一天；兩者皆存 hh='23'）
// hh 值依據 0_PreLoad.r：WestHour=23→子時，WestHour=1→丑時，…，WestHour=21→亥時
interface HourOption { value: string; label: string; hourCode: string; prevDay: boolean; }
const HOUR_OPTIONS: HourOption[] = [
  { value: '23_prev', label: '子時 (00:01-01:00)',  hourCode: '23', prevDay: true  },
  { value: '01',      label: '丑時 (01:00-03:00)',  hourCode: '01', prevDay: false },
  { value: '03',      label: '寅時 (03:00-05:00)',  hourCode: '03', prevDay: false },
  { value: '05',      label: '卯時 (05:00-07:00)',  hourCode: '05', prevDay: false },
  { value: '07',      label: '辰時 (07:00-09:00)',  hourCode: '07', prevDay: false },
  { value: '09',      label: '巳時 (09:00-11:00)',  hourCode: '09', prevDay: false },
  { value: '11',      label: '午時 (11:00-13:00)',  hourCode: '11', prevDay: false },
  { value: '13',      label: '未時 (13:00-15:00)',  hourCode: '13', prevDay: false },
  { value: '15',      label: '申時 (15:00-17:00)',  hourCode: '15', prevDay: false },
  { value: '17',      label: '酉時 (17:00-19:00)',  hourCode: '17', prevDay: false },
  { value: '19',      label: '戌時 (19:00-21:00)',  hourCode: '19', prevDay: false },
  { value: '21',      label: '亥時 (21:00-23:00)',  hourCode: '21', prevDay: false },
  { value: '23',      label: '子時 (23:01-24:00)',  hourCode: '23', prevDay: false },
];

// ================================================================
// 輔助函數
// ================================================================

function formatBirthDate(birthDatetime: string): string {
  if (!birthDatetime || birthDatetime.length < 8) return birthDatetime;
  const y = birthDatetime.substring(0, 4);
  const m = parseInt(birthDatetime.substring(4, 6), 10);
  const d = parseInt(birthDatetime.substring(6, 8), 10);
  const h = birthDatetime.length >= 10 ? parseInt(birthDatetime.substring(8, 10), 10) : null;
  return `${y} 年 ${m} 月 ${d} 日${h !== null ? ` ${String(h).padStart(2, '0')}:00` : ''}`;
}

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' });
  } catch {
    return dateStr;
  }
}

/** 將 YYYY-MM-DD + hourValue 轉成 YYYYMMDDhh（子時前段自動轉前一天）*/
function buildBirthTime(date: string, hourValue: string): string {
  const opt = HOUR_OPTIONS.find((o) => o.value === hourValue);
  const hourCode = opt ? opt.hourCode : hourValue;
  const prevDay = opt ? opt.prevDay : false;
  if (prevDay) {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}${mm}${dd}${hourCode}`;
  }
  return date.replace(/-/g, '') + hourCode;
}

function isPairMulti(questionId: string): boolean {
  const num = parseInt(questionId.replace(/\D/g, ''), 10);
  return num >= 26;
}

// ================================================================
// 個案資訊卡
// ================================================================

interface ConsulteeInfoCardProps {
  consultee: Consultee;
}

const ConsulteeInfoCard: React.FC<ConsulteeInfoCardProps> = ({ consultee }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
        {consultee.gender === 1 ? (
          <Male sx={{ color: 'primary.main', fontSize: 32 }} />
        ) : (
          <Female sx={{ color: 'secondary.main', fontSize: 32 }} />
        )}
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {consultee.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {consultee.gender === 1 ? '男' : '女'} ・ 生於 {formatBirthDate(consultee.birth_datetime)}
          </Typography>
        </Box>
        <Box ml="auto">
          <Chip
            label={`${consultee.consultation_count} 次諮詢`}
            color="secondary"
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>
      {consultee.notes && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 0.5 }}>
          備注：{consultee.notes}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// ================================================================
// 單人輸入表單（B 方或單個候選人）
// ================================================================

interface PersonFormValue {
  name: string;
  date: string;  // YYYY-MM-DD
  hour: string;  // HOUR_OPTIONS 的 value（含 '23_prev'）
  gender: number; // 1=男 0=女
}

const DEFAULT_PERSON: PersonFormValue = {
  name: '',
  date: '',
  hour: '11',  // 午時（hh='11', 11:00-13:00）
  gender: 1,
};

interface PersonFormProps {
  label: string;
  value: PersonFormValue;
  onChange: (v: PersonFormValue) => void;
  onRemove?: () => void;
}

const PersonForm: React.FC<PersonFormProps> = ({ label, value, onChange, onRemove }) => (
  <Box
    sx={{
      p: 2,
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      mb: 1.5,
      bgcolor: 'background.paper',
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
      <Typography variant="subtitle2" fontWeight="bold" color="primary">
        {label}
      </Typography>
      {onRemove && (
        <IconButton size="small" onClick={onRemove} color="error">
          <Delete fontSize="small" />
        </IconButton>
      )}
    </Box>

    <Box display="flex" gap={1.5} flexWrap="wrap">
      <TextField
        label="姓名／代稱"
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        size="small"
        sx={{ width: 140 }}
      />
      <TextField
        label="出生日期"
        type="date"
        value={value.date}
        onChange={(e) => onChange({ ...value, date: e.target.value })}
        size="small"
        InputLabelProps={{ shrink: true }}
        sx={{ width: 170 }}
      />
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>出生時辰</InputLabel>
        <Select
          value={value.hour}
          label="出生時辰"
          onChange={(e) => onChange({ ...value, hour: e.target.value })}
        >
          {HOUR_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>性別</FormLabel>
        <RadioGroup
          row
          value={value.gender}
          onChange={(e) => onChange({ ...value, gender: parseInt(e.target.value, 10) })}
        >
          <FormControlLabel value={1} control={<Radio size="small" />} label="男" />
          <FormControlLabel value={0} control={<Radio size="small" />} label="女" />
        </RadioGroup>
      </FormControl>
    </Box>
  </Box>
);

function toPersonInfo(pf: PersonFormValue): PersonInfo {
  return {
    birth_time: buildBirthTime(pf.date, pf.hour),
    name: pf.name || undefined,
    gender: pf.gender,
  };
}

function validatePerson(pf: PersonFormValue): string | null {
  if (!pf.date) return '請填寫出生日期';
  const bt = buildBirthTime(pf.date, pf.hour);
  if (!/^\d{10}$/.test(bt)) return '出生日期格式錯誤';
  return null;
}

// ================================================================
// 配對區塊（PAIR）
// ================================================================

interface PairSectionProps {
  consulteeId: number;
  onAnswered: () => void;
}

const PairSection: React.FC<PairSectionProps> = ({ consulteeId, onAnswered }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<PairResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const answerRef = useRef<HTMLDivElement>(null);

  // 雙人配對 B 方
  const [personB, setPersonB] = useState<PersonFormValue>({ ...DEFAULT_PERSON });
  // 多人選一候選人
  const [candidates, setCandidates] = useState<PersonFormValue[]>([{ ...DEFAULT_PERSON }]);

  useEffect(() => {
    setQuestionsLoading(true);
    getQuestions('PAIR')
      .then((res) => setQuestions(res.questions || []))
      .catch(() => setQuestions([]))
      .finally(() => setQuestionsLoading(false));
  }, []);

  const filteredQuestions = questions.filter(
    (q) => !searchText || q.question_text.includes(searchText)
  );

  const selectQuestion = (q: Question) => {
    setSelectedQuestion(q);
    setResult(null);
    setError(null);
    // 重置輸入
    setPersonB({ ...DEFAULT_PERSON });
    setCandidates([{ ...DEFAULT_PERSON }]);
  };

  const isMulti = selectedQuestion ? isPairMulti(selectedQuestion.question_id) : false;

  const addCandidate = () => {
    if (candidates.length < 5) {
      setCandidates((prev) => [...prev, { ...DEFAULT_PERSON }]);
    }
  };

  const removeCandidate = (idx: number) => {
    setCandidates((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRun = async () => {
    if (!selectedQuestion) return;
    setError(null);
    setResult(null);

    if (!isMulti) {
      const err = validatePerson(personB);
      if (err) { setError(`B 方：${err}`); return; }
    } else {
      for (let i = 0; i < candidates.length; i++) {
        const err = validatePerson(candidates[i]);
        if (err) { setError(`候選人 ${i + 1}：${err}`); return; }
      }
    }

    setIsRunning(true);
    try {
      let res: PairResponse;
      if (!isMulti) {
        res = await teacherPairAnalysis(consulteeId, {
          question_id: selectedQuestion.question_id,
          person_b: toPersonInfo(personB),
        });
      } else {
        res = await teacherPairAnalysis(consulteeId, {
          question_id: selectedQuestion.question_id,
          candidates: candidates.map(toPersonInfo),
        });
      }

      if (!res.success) {
        setError((res as any).message || '配對分析失敗');
        return;
      }
      setResult(res);
      onAnswered();
      setTimeout(() => {
        answerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || '配對分析失敗');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Favorite sx={{ color: '#e91e8c' }} />
          <Typography variant="h6" fontWeight="bold">
            配對分析（不扣款）
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          此個案為「主方」，請依選取的配對問題填入對方或候選人的出生資料。配對分析不扣款且自動記錄到諮詢歷史。
        </Alert>

        {/* 搜尋欄 */}
        <TextField
          fullWidth
          placeholder="篩選配對問題..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* 問題列表 */}
        <Box
          sx={{
            maxHeight: 230,
            overflowY: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
          }}
        >
          {questionsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredQuestions.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }} variant="body2">
              無符合的問題
            </Typography>
          ) : (
            <List disablePadding dense>
              {filteredQuestions.map((q, idx) => (
                <React.Fragment key={q.question_id}>
                  {idx > 0 && <Divider />}
                  <ListItemButton
                    selected={selectedQuestion?.question_id === q.question_id}
                    onClick={() => selectQuestion(q)}
                  >
                    <ListItemText
                      primary={q.question_text}
                      secondary={q.question_id}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    {isPairMulti(q.question_id) && (
                      <Chip label="多人選一" size="small" variant="outlined" sx={{ ml: 1 }} />
                    )}
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* 選中問題說明 */}
        {selectedQuestion && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'action.selected',
              borderRadius: 1,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Chip
              label={selectedQuestion.question_id}
              size="small"
              color="secondary"
              variant="filled"
            />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {selectedQuestion.question_text}
            </Typography>
            <Chip
              label={isMulti ? '多人選一（1-5 人）' : '雙人配對'}
              size="small"
              variant="outlined"
              sx={{ color: '#e91e8c', borderColor: '#e91e8c' }}
            />
          </Box>
        )}

        {/* 資料輸入區 */}
        {selectedQuestion && (
          <Box sx={{ mb: 2 }}>
            {!isMulti ? (
              // 雙人配對：B 方輸入
              <PersonForm
                label="B 方（對象）出生資料"
                value={personB}
                onChange={setPersonB}
              />
            ) : (
              // 多人選一：候選人列表
              <>
                {candidates.map((c, i) => (
                  <PersonForm
                    key={i}
                    label={`候選人 ${i + 1}`}
                    value={c}
                    onChange={(v) => {
                      const arr = [...candidates];
                      arr[i] = v;
                      setCandidates(arr);
                    }}
                    onRemove={candidates.length > 1 ? () => removeCandidate(i) : undefined}
                  />
                ))}
                {candidates.length < 5 && (
                  <Button
                    startIcon={<PersonAdd />}
                    onClick={addCandidate}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{ mb: 1 }}
                  >
                    新增候選人（最多 5 人）
                  </Button>
                )}
              </>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <Favorite />}
          onClick={handleRun}
          disabled={!selectedQuestion || isRunning}
          fullWidth
          sx={{ background: 'linear-gradient(135deg, #e91e8c, #9c27b0)' }}
        >
          {isRunning ? '配對分析中...' : '開始配對分析'}
        </Button>

        {isRunning && (
          <LinearProgress
            sx={{ mt: 1, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#e91e8c' } }}
          />
        )}

        {/* 結果區域 */}
        {result && (
          <Box ref={answerRef} sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Favorite sx={{ color: '#e91e8c' }} />
              <Typography variant="subtitle1" fontWeight="bold">
                配對分析結果
              </Typography>
              <Chip
                label={result.question_id}
                size="small"
                variant="outlined"
              />
            </Box>

            {result.is_multi_pair ? (
              // 多人選一：分候選人顯示
              (result as any).results.map((r: any, i: number) => (
                <Accordion
                  key={i}
                  defaultExpanded={i === 0}
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={`候選人 ${i + 1}`}
                        size="small"
                        color="secondary"
                        variant="filled"
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {r.candidate_name}
                      </Typography>
                      {r.result?.compatibility_score != null && (
                        <Chip
                          label={`${Number(r.result.compatibility_score).toFixed(1)} 分`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Divider sx={{ mb: 1.5 }} />
                    <ReportContent text={r.output} />
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              // 雙人配對：直接顯示
              <ReportContent text={(result as any).output} />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ================================================================
// 問問題區塊（非 PAIR 類別）
// ================================================================

interface AskSectionProps {
  consulteeId: number;
  onAnswered: () => void;
}

const AskSection: React.FC<AskSectionProps> = ({ consulteeId, onAnswered }) => {
  const [activeCategory, setActiveCategory] = useState<QuestionCategory>('MAIN');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [isAsking, setIsAsking] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const answerRef = useRef<HTMLDivElement>(null);

  const loadQuestions = useCallback(async (cat: QuestionCategory) => {
    setQuestionsLoading(true);
    try {
      const res = await getQuestions(cat);
      setQuestions(res.questions || []);
    } catch {
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuestions(activeCategory);
    setSelectedQuestion(null);
    setAnswer(null);
    setError(null);
    setSearchText('');
  }, [activeCategory, loadQuestions]);

  const filteredQuestions = questions.filter((q) =>
    !searchText || q.question_text.includes(searchText)
  );

  const handleAsk = async () => {
    if (!selectedQuestion) return;
    setError(null);
    setAnswer(null);
    setIsAsking(true);
    try {
      const res = await askQuestion(consulteeId, {
        question_id: selectedQuestion.question_id,
      });
      if (!res.success) {
        setError(res.message || '分析失敗');
        return;
      }
      setAnswer(res.answer || '');
      onAnswered();
      setTimeout(() => {
        answerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || '分析失敗');
    } finally {
      setIsAsking(false);
    }
  };

  const NON_PAIR_CATS = CATEGORIES.filter((c) => c.id !== 'PAIR');
  const isFull = activeCategory === 'FULL';

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          問問題
        </Typography>

        {/* 類別 Tabs（排除 PAIR） */}
        <Tabs
          value={activeCategory}
          onChange={(_, v) => setActiveCategory(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {NON_PAIR_CATS.map((c) => (
            <Tab key={c.id} value={c.id} label={c.label} />
          ))}
        </Tabs>

        {/* 搜尋欄 */}
        <TextField
          fullWidth
          placeholder="篩選問題..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* 問題列表 */}
        <Box
          sx={{
            maxHeight: 260,
            overflowY: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
          }}
        >
          {questionsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredQuestions.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }} variant="body2">
              無符合的問題
            </Typography>
          ) : (
            <List disablePadding dense>
              {filteredQuestions.map((q, idx) => (
                <React.Fragment key={q.question_id}>
                  {idx > 0 && <Divider />}
                  <ListItemButton
                    selected={selectedQuestion?.question_id === q.question_id}
                    onClick={() => {
                      setSelectedQuestion(q);
                      setAnswer(null);
                      setError(null);
                    }}
                  >
                    <ListItemText
                      primary={q.question_text}
                      secondary={q.question_id}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* 選中問題 + 執行按鈕 */}
        {selectedQuestion && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'action.selected',
              borderRadius: 1,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Chip
              label={selectedQuestion.question_id}
              size="small"
              color="secondary"
              variant="filled"
            />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {selectedQuestion.question_text}
            </Typography>
          </Box>
        )}

        {isFull && selectedQuestion && (
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            FULL 類完整分析需要較長時間（30–90 秒），請耐心等候。
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          color="secondary"
          size="large"
          startIcon={isAsking ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
          onClick={handleAsk}
          disabled={!selectedQuestion || isAsking}
          fullWidth
        >
          {isAsking ? '分析中...' : '開始分析'}
        </Button>

        {isAsking && (
          <LinearProgress color="secondary" sx={{ mt: 1, borderRadius: 1 }} />
        )}

        {/* 答案區域 */}
        {answer && (
          <Box ref={answerRef} sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <AutoAwesome color="secondary" />
              <Typography variant="subtitle1" fontWeight="bold">
                分析結果
              </Typography>
              {selectedQuestion && (
                <Chip
                  label={selectedQuestion.question_id}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            <ReportContent text={answer} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ================================================================
// 歷史記錄區塊
// ================================================================

interface HistorySectionProps {
  consulteeId: number;
  refreshKey: number;
  consulteeName: string;
}

const exportMarkdown = (item: Consultation, consulteeName: string) => {
  const lines: string[] = [];
  lines.push(`# 諮詢報告`);
  lines.push(``);
  lines.push(`- **個案**：${consulteeName}`);
  lines.push(`- **問題**：${item.question_id}${item.question_text ? ` — ${item.question_text}` : ''}`);
  lines.push(`- **時間**：${item.consulted_at}`);
  lines.push(``);
  lines.push(`---`);
  lines.push(``);
  lines.push(item.answer || '（無報告內容）');

  const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeDate = item.consulted_at.replace(/[: ]/g, '-').slice(0, 19);
  a.href = url;
  a.download = `${consulteeName}_${item.question_id}_${safeDate}.md`;
  a.click();
  URL.revokeObjectURL(url);
};

const HistorySection: React.FC<HistorySectionProps> = ({
  consulteeId,
  refreshKey,
  consulteeName,
}) => {
  const [history, setHistory] = useState<Consultation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | false>(false);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await getConsultationHistory(consulteeId);
      if (res.success) {
        setHistory(res.history || []);
      } else {
        setError(res.message || '載入失敗');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [consulteeId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshKey]);

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <History color="action" />
          <Typography variant="h6" fontWeight="bold">
            諮詢歷史
          </Typography>
          {history.length > 0 && (
            <Chip label={`${history.length} 筆`} size="small" variant="outlined" />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : history.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            尚無諮詢記錄
          </Typography>
        ) : (
          history.map((item) => (
            <Accordion
              key={item.id}
              expanded={expandedId === item.id}
              onChange={(_, open) => setExpandedId(open ? item.id : false)}
              disableGutters
              elevation={0}
              sx={{
                mb: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1} flex={1} flexWrap="wrap">
                  <Chip
                    label={item.question_id}
                    size="small"
                    color={item.question_id?.startsWith('PAIR') ? 'error' : 'secondary'}
                    variant="outlined"
                  />
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {item.question_text || item.question_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', mr: 1 }}>
                    {formatDateTime(item.consulted_at)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Divider sx={{ mb: 2 }} />
                {item.answer ? (
                  <>
                    <ReportContent text={item.answer} />
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => exportMarkdown(item, consulteeName)}
                      >
                        輸出報告
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    無記錄內容
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </CardContent>
    </Card>
  );
};

// ================================================================
// 主頁面
// ================================================================

const TeacherConsulteePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { teacher, logout } = useTeacher();

  const [consultee, setConsultee] = useState<Consultee | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  // 主 Tab：0=問問題  1=配對分析
  const [mainTab, setMainTab] = useState(0);

  const consulteeId = parseInt(id || '0', 10);

  useEffect(() => {
    if (!consulteeId) return;
    setIsLoading(true);
    setError(null);
    getConsultee(consulteeId)
      .then((res) => {
        if (res.success && res.consultee) {
          setConsultee(res.consultee);
        } else {
          setError(res.message || '個案不存在');
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || '載入失敗');
      })
      .finally(() => setIsLoading(false));
  }, [consulteeId]);

  const handleAnswered = () => {
    getConsultee(consulteeId).then((res) => {
      if (res.success && res.consultee) setConsultee(res.consultee);
    });
    setHistoryRefreshKey((k) => k + 1);
  };

  const handleLogout = () => {
    logout();
    navigate('/teacher/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* AppBar */}
      <AppBar
        position="sticky"
        sx={{ background: 'linear-gradient(135deg, #2d1b69 0%, #7b2d8b 100%)' }}
        elevation={2}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/teacher/dashboard')}
            sx={{ mr: 1 }}
          >
            <ArrowBack />
          </IconButton>
          <AutoAwesome sx={{ mr: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
            {consultee ? consultee.name : '個案詳情'}
          </Typography>
          {teacher && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Person fontSize="small" />
              <Typography variant="body2" sx={{ mr: 1 }}>
                {teacher.display_name || teacher.username}
              </Typography>
            </Box>
          )}
          <IconButton color="inherit" onClick={handleLogout} title="登出">
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress color="secondary" />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" onClick={() => navigate('/teacher/dashboard')}>
                返回列表
              </Button>
            }
          >
            {error}
          </Alert>
        ) : consultee ? (
          <>
            {/* 個案資訊卡 */}
            <ConsulteeInfoCard consultee={consultee} />

            {/* 主 Tab */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={mainTab}
                onChange={(_, v) => setMainTab(v)}
              >
                <Tab label="問問題" />
                <Tab
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Favorite sx={{ fontSize: 16, color: '#e91e8c' }} />
                      配對分析
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {mainTab === 0 && (
              <AskSection consulteeId={consulteeId} onAnswered={handleAnswered} />
            )}
            {mainTab === 1 && (
              <PairSection consulteeId={consulteeId} onAnswered={handleAnswered} />
            )}

            {/* 歷史記錄 */}
            <HistorySection
              consulteeId={consulteeId}
              refreshKey={historyRefreshKey}
              consulteeName={consultee?.name || ''}
            />
          </>
        ) : null}
      </Container>
    </Box>
  );
};

export default TeacherConsulteePage;
